import { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Box, 
  Toolbar, 
  Typography, 
  Button, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemText, 
  Container, 
  useMediaQuery, 
  useTheme,
  Avatar,
  Menu,
  MenuItem,
  Grid
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ArticleIcon from '@mui/icons-material/Article';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LogoutIcon from '@mui/icons-material/Logout';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import { useAuthStore } from '../../store/authStore';
import Logo from '../common/Logo';

const MainLayout = () => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
    navigate('/');
  };

  const handleAdminPanel = () => {
    handleMenuClose();
    navigate('/admin');
  };

  const menuItems = [
    { text: 'Главная', path: '/', icon: <HomeIcon /> },
    { text: 'Галерея', path: '/gallery', icon: <PhotoLibraryIcon /> },
    { text: 'Видео', path: '/videos', icon: <VideoLibraryIcon /> },
    { text: 'Отзывы', path: '/testimonials', icon: <RateReviewIcon /> },
    { text: 'Блог', path: '/blog', icon: <ArticleIcon /> },
    { text: 'Полезные советы', path: '/tips', icon: <LightbulbIcon /> },
    { text: 'Услуги', path: '/services', icon: <MiscellaneousServicesIcon /> },
    { text: 'Забронировать', path: '/booking', icon: <BookOnlineIcon /> }
  ];

  const drawer = (
    <Box sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ my: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Logo variant="small" />
      </Box>
      <List sx={{ flexGrow: 1 }}>
        {menuItems.map((item) => (
          <ListItem 
            key={item.text} 
            component={Link} 
            to={item.path}
            onClick={handleDrawerToggle} // Закрываем меню при клике на пункт меню
            sx={{ 
              textAlign: 'center',
              '&:hover': {
                backgroundColor: 'rgba(46, 91, 136, 0.08)'
              },
              py: 1.5, // Увеличиваем высоту пункта меню на мобильных устройствах
              fontSize: '1.1rem' // Увеличиваем размер шрифта
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
              <Box sx={{ mr: 2, color: theme.palette.primary.main }}>{item.icon}</Box>
              <ListItemText primary={item.text} />
            </Box>
          </ListItem>
        ))}
        {/* DISABLED: кнопки Войти/Регистрация скрыты. Вход в админку через /login напрямую */}
        {user && (
          <>
            {(user.role === 'admin' || user.role === 'superadmin') && (
              <ListItem 
                component={Link} 
                to="/admin"
                onClick={handleDrawerToggle}
                sx={{ 
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(46, 91, 136, 0.08)'
                  },
                  py: 1.5,
                  fontSize: '1.1rem'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                  <Box sx={{ mr: 2, color: theme.palette.primary.main }}><DashboardIcon /></Box>
                  <ListItemText primary="Панель администратора" />
                </Box>
              </ListItem>
            )}
            <ListItem 
              component={Link} 
              to="/settings"
              onClick={handleDrawerToggle}
              sx={{ 
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(46, 91, 136, 0.08)'
                },
                py: 1.5,
                fontSize: '1.1rem'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                <Box sx={{ mr: 2, color: theme.palette.primary.main }}><SettingsIcon /></Box>
                <ListItemText primary="Настройки пользователя" />
              </Box>
            </ListItem>
            <ListItem 
              component={Link} 
              to="/my-coupons"
              onClick={handleDrawerToggle}
              sx={{ 
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(46, 91, 136, 0.08)'
                },
                py: 1.5,
                fontSize: '1.1rem'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                <Box sx={{ mr: 2, color: theme.palette.primary.main }}><LocalOfferIcon /></Box>
                <ListItemText primary="Мои купоны" />
              </Box>
            </ListItem>
            <ListItem 
              onClick={(e) => {
                handleLogout();
                handleDrawerToggle();
              }}
              sx={{ 
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(46, 91, 136, 0.08)'
                },
                py: 1.5,
                fontSize: '1.1rem'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                <Box sx={{ mr: 2, color: theme.palette.primary.main }}><LogoutIcon /></Box>
                <ListItemText primary="Выйти" />
              </Box>
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar 
        position="absolute" 
        color="default" 
        elevation={0} // Убираем встроенное возвышение
        sx={{
          zIndex: 1200, // Увеличиваем z-index еще больше
          backgroundColor: (theme) => 
            theme.palette.mode === 'dark' 
              ? 'rgba(18, 18, 18, 0.98)' // Более непрозрачный темный фон
              : 'rgba(255, 255, 255, 0.98)', // Более непрозрачный светлый фон
          backdropFilter: 'blur(10px)', // Усиливаем размытие
          boxShadow: 'none', // Убираем тень полностью
          borderBottom: 'none', // Убираем границу снизу
          top: 0, // Закрепляем вверху страницы
          left: 0,
          right: 0,
        }}
      >
        <Toolbar>
          <Box sx={{ flexGrow: 1 }}>
            <Logo />
          </Box>

          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {menuItems.map((item) => (
                <Button
                  key={item.text}
                  component={Link}
                  to={item.path}
                  startIcon={item.icon}
                  sx={{ 
                    mx: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(46, 91, 136, 0.08)'
                    }
                  }}
                >
                  {item.text}
                </Button>
              ))}
              
              {/* DISABLED: кнопки Войти/Регистрация скрыты. Вход в админку через /login напрямую */}
              {user && (
                <>
                  <IconButton onClick={handleMenuOpen}>
                    <Avatar 
                      sx={{ 
                        bgcolor: theme.palette.primary.main,
                        width: 35,
                        height: 35
                      }}
                    >
                      {user.name.charAt(0).toUpperCase()}
                    </Avatar>
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                  >
                    <MenuItem disabled>
                      <Typography variant="body2">
                        {user.name}
                      </Typography>
                    </MenuItem>
                    {(user.role === 'admin' || user.role === 'superadmin') && (
                      <MenuItem onClick={handleAdminPanel}>
                        Панель администратора
                      </MenuItem>
                    )}
                    <MenuItem onClick={() => { handleMenuClose(); navigate('/settings'); }}>
                      Настройки профиля
                    </MenuItem>
                    <MenuItem onClick={() => { handleMenuClose(); navigate('/my-coupons'); }}>
                      Мои купоны
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>Выйти</MenuItem>
                  </Menu>
                </>
              )}
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: '100%', // На весь экран на мобильных устройствах
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: '16px 0'
          },
          zIndex: theme.zIndex.appBar + 100 // Устанавливаем высокий z-index для меню
        }}
      >
        {drawer}
      </Drawer>

      {/* Добавляем пустой блок для отступа под фиксированный хедер - только для страниц, кроме главной */}
      {location.pathname !== '/' && (
        <Box sx={{ height: { xs: '64px', sm: '72px' }, mb: { xs: 1, sm: 2 } }} />
      )}
      
      <Box component="main" sx={{ flexGrow: 1 }}>
        <Outlet />
      </Box>

      <Box
        component="footer"
        sx={{
          py: 4,
          px: 2,
          mt: 'auto',
          backgroundColor: (theme) => theme.palette.mode === 'dark' 
            ? theme.palette.background.default 
            : theme.palette.background.paper,
          borderTop: '1px solid',
          borderColor: (theme) => theme.palette.divider,
          boxShadow: '0px -2px 10px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Container maxWidth="lg">
          {/* Верхняя часть футера с логотипами и описанием */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Логотип и описание */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Logo variant="small" />
                  <Typography variant="h6" color="text.primary" sx={{ ml: 1, fontWeight: 600 }}>
                    DomikTyumen
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Сервис бронирования загородных домов и коттеджей в Тюмени
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  © {new Date().getFullYear()} DomikTyumen. Все права защищены.
                </Typography>
              </Box>
            </Grid>
            
            {/* Контакты и лого разработчика */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', md: 'flex-end' }, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box component="img" 
                    src="/assets/stanleydapps_logo.png" 
                    alt="StanleyD Apps" 
                    sx={{ 
                      height: 45, 
                      mr: 1,
                      filter: (theme) => theme.palette.mode === 'dark' ? 'brightness(1.2)' : 'none',
                      transition: 'filter 0.3s ease'
                    }} 
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: { xs: 'left', md: 'right' } }}>
                  Разработано StanleyD Apps
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, mt: 'auto', pt: 1 }}>
                  <Typography 
                    variant="body2" 
                    component={Link} 
                    to="/privacy" 
                    sx={{ 
                      textDecoration: 'none', 
                      color: 'text.secondary',
                      '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                      transition: 'color 0.3s ease'
                    }}
                  >
                    Политика конфиденциальности
                  </Typography>
                  <Typography 
                    variant="body2" 
                    component={Link} 
                    to="/terms" 
                    sx={{ 
                      textDecoration: 'none', 
                      color: 'text.secondary',
                      '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                      transition: 'color 0.3s ease'
                    }}
                  >
                    Условия использования
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default MainLayout;
